import React from 'react'
import p5 from "p5"

export default class ReactP5Wrapper extends React.Component {
  // Identifies the current delivery attempt, so a sketch that is replaced while
  // we are still waiting for setup does not get props meant for the old one.
  _deliveryId = 0;
  _notified = false;

  /**
   * Report, once, that this sketch has finished its first frame.
   *
   * A grid uses this to decide when it is safe to construct the next sketch.
   * Delivery of props is not a late enough signal: scripts like Ethereal Phase
   * read shared globals from draw() as well as from the props handler, so a
   * cell can still be drawing when the next one overwrites them.
   */
  notifyReady() {
    if (this._notified) return;
    this._notified = true;
    if (typeof this.props.onSketchReady === 'function') {
      try {
        this.props.onSketchReady();
      } catch (e) {
        // A listener failing is not this sketch's problem.
      }
    }
  }

  componentDidMount() {
    this.canvas = this.createSketch()
    this.patchCanvas()
    this.deliverProps(this.props)
  }

  shouldComponentUpdate(nextProps) {
    return this.props.sketch !== nextProps.sketch || this.props.square !== nextProps.square;
  }

  componentDidUpdate(prevProps) {
    if(prevProps.sketch !== this.props.sketch){
      this.cleanupCanvas()
      this.canvas = this.createSketch()
      this.patchCanvas()
    }
    this.deliverProps(this.props)
  }

  createSketch() {
    return new p5(instance => {
      // This p5 version inherits the viewport measured when the library loaded.
      // A sketch mounted after fullscreen/resize needs today's dimensions before
      // its source or setup runs, without waiting for another resize event.
      instance.windowWidth = window.innerWidth;
      instance.windowHeight = window.innerHeight;
      this.props.sketch(instance);
    }, this.wrapper);
  }

  /**
   * Hand the sketch its props, but not before p5 has finished setup.
   *
   * Where a script calls createCanvas decides how this breaks. Syncret and
   * Firmament call it in setup and then resize from the handler; BigBang and
   * the rest call it in the handler itself. Either way, if props arrive first,
   * p5 runs setup afterwards and overwrites the result — leaving Syncret and
   * Firmament at the 1x1 canvas their setup creates, and BigBang at p5's blank
   * 100x100 default. Nothing re-delivers, because shouldComponentUpdate only
   * fires again when the sketch or square changes.
   *
   * Whether props win that race is purely a matter of load timing, which is why
   * the served site rendered correctly while the single-file bundle — with all
   * of its data inlined and nothing to wait for — did not.
   */
  deliverProps(props) {
    const instance = this.canvas;
    if (!instance) return;

    const call = () => {
      const handler = instance.myCustomRedrawAccordingToNewPropsHandler;
      if (typeof handler !== 'function') return;
      try {
        handler.call(instance, props);
      } catch (e) {
        // Art scripts are untrusted and must not take the page down.
      }
    };

    // Setup has run once a canvas exists in our wrapper: p5 creates one there
    // either because the script called createCanvas or because p5 fell back to
    // its own default. That is observable and version-independent, unlike the
    // _setupDone flag, which is private to p5 and absent on some versions —
    // reading it and finding undefined would look like "setup finished" and
    // reintroduce the very race this guards against.
    const setupHasRun = () => !!(this.wrapper && this.wrapper.querySelector('canvas'));

    if (setupHasRun()) {
      call();
      return;
    }

    const id = ++this._deliveryId;
    let frames = 0;
    const waitForSetup = () => {
      // Abandoned if the sketch was swapped or a newer delivery superseded this.
      if (this.canvas !== instance || id !== this._deliveryId) return;
      // Deliver late rather than never if setup somehow stalls.
      if (setupHasRun() || ++frames > 600) {
        call();
        return;
      }
      window.requestAnimationFrame(waitForSetup);
    };
    window.requestAnimationFrame(waitForSetup);
  }

  // Patch the p5 instance to guard against errors in on-chain art scripts
  patchCanvas() {
    if (!this.canvas) return;
    const instance = this.canvas;
    // Kept because `this` inside the property setters below is the p5 instance.
    const component = this;

    // Bridge the react-p5-wrapper v4 API onto this v2-style wrapper.
    //
    // Some on-chain scripts define myCustomRedrawAccordingToNewPropsHandler (the
    // v2 API this wrapper calls) but replay cached props through
    // p.updateWithProps (the v4 API):
    //
    //   handler = (props) => { if (!isSetup) { propCache = props; return; } ... }
    //   p.setup = () => { isSetup = true; if (propCache) p.updateWithProps(propCache); }
    //
    // Nothing in the dependency tree defines updateWithProps, so that call throws
    // inside setup. Because the script does its createCanvas work in the handler,
    // the sketch is then left as p5's blank 100x100 default. It only happens when
    // the wrapper delivers props before p5 runs setup, which is load-timing
    // dependent — fast local serving triggers it, the network usually does not.
    //
    // Defining the alias makes the replay path work and removes the race.
    if (typeof instance.updateWithProps !== 'function') {
      let replaying = false;
      instance.updateWithProps = function(props) {
        // A script could call updateWithProps from inside its own handler.
        if (replaying) return;
        const handler = instance.myCustomRedrawAccordingToNewPropsHandler;
        if (typeof handler !== 'function') return;
        replaying = true;
        try {
          return handler.call(instance, props);
        } catch (e) {
          // Same tolerance as the draw() wrapper below: art scripts are
          // untrusted and must not take the page down.
        } finally {
          replaying = false;
        }
      };
    }

    // Make p.image() null-safe — scripts may call p.image() before loadImage resolves
    const origImage = instance.image.bind(instance);
    instance.image = function(img) {
      if (!img) return;
      return origImage.apply(this, arguments);
    };

    // Wrap draw() in try-catch — on-chain scripts can reference null DOM elements
    // or other transient state. Use defineProperty so any later reassignment of
    // p.draw (e.g. in setup or myCustomRedrawAccordingToNewPropsHandler) is also wrapped.
    let rawDraw = instance.draw;
    Object.defineProperty(instance, 'draw', {
      get() { return rawDraw; },
      set(fn) {
        if (typeof fn === 'function') {
          const wrapped = function() {
            try { fn.call(instance); } catch(e) { /* suppress draw errors */ }
            // The first completed frame is the point at which this sketch is
            // done with the globals it shares with its siblings.
            component.notifyReady();
          };
          wrapped._original = fn;
          rawDraw = wrapped;
        } else {
          rawDraw = fn;
        }
      },
      configurable: true
    });
    // Re-wrap the current draw if already set
    if (typeof rawDraw === 'function') {
      instance.draw = rawDraw;
    }
  }

  cleanupCanvas() {
    if (!this.canvas) return;
    try {
      this.canvas.noLoop();
      if (this.wrapper) {
        const glCanvas = this.wrapper.querySelector('canvas');
        if (glCanvas) {
          const gl = glCanvas.getContext('webgl2') || glCanvas.getContext('webgl');
          if (gl) {
            const ext = gl.getExtension('WEBGL_lose_context');
            if (ext) ext.loseContext();
          }
        }
      }
      this.canvas.remove();
    } catch (e) {
      // Ignore cleanup errors
    }
  }

  componentWillUnmount() {
    this.cleanupCanvas()
  }

  render() {
    return <div ref={wrapper => this.wrapper = wrapper}></div>
  }
}
